import { addToCart, formatMoney } from '@theme/ilm-cart';

/**
 * Shoppable reels.
 *
 * <ilm-reels> renders a rail or grid of vertical video cards (YouTube Shorts) for its context
 * (all reels, one product, or the products of a collection). Clicking a card opens a single
 * shared full-screen viewer: a vertical, snap-scrolling feed that plays one reel at a time,
 * shows the linked book with "Add to cart" / "View book", and continues with random reels.
 *
 * Data sources, merged and de-duplicated by video id:
 *  1. Shopify metaobjects (`shoppable_videos`) rendered by Liquid into a JSON script.
 *  2. An optional starter JSON asset (data-fallback-url) listing the store's YouTube Shorts.
 *
 * This element renders data-driven markup on the client, so it is a plain custom element
 * rather than a Component with declarative refs.
 */

/** @typedef {{ id: string, t: string, p: string[] }} Reel */
/** @typedef {{ handle: string, title: string, url: string, price: number, compare: number, image: string, variantId: number, available: boolean, multiple: boolean }} Book */

const THUMB = (id) => `https://i.ytimg.com/vi/${id}/oar2.jpg`;
const THUMB_FALLBACK = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

/** @type {Map<string, Promise<Book | null>>} */
const bookCache = new Map();

/** @param {string} handle */
function fetchBook(handle) {
  if (!handle) return Promise.resolve(null);
  if (!bookCache.has(handle)) {
    const root = window.Shopify?.routes?.root || '/';
    bookCache.set(
      handle,
      fetch(`${root}products/${encodeURIComponent(handle)}.js`)
        .then((response) => (response.ok ? response.json() : null))
        .then((product) => {
          if (!product) return null;
          const variant = product.variants.find((v) => v.available) || product.variants[0];
          return {
            handle,
            title: product.title,
            url: product.url,
            price: variant.price,
            compare: variant.compare_at_price || 0,
            image: product.featured_image ? `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=200` : '',
            variantId: variant.id,
            available: product.available,
            multiple: product.variants.length > 1,
          };
        })
        .catch(() => null)
    );
  }
  return /** @type {Promise<Book | null>} */ (bookCache.get(handle));
}

/** @param {number} cents */
/** @param {number} cents */
const money = (cents) => formatMoney(cents);

/** @param {string} value */
function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c);
}

/** @template T @param {T[]} list */
function shuffle(list) {
  const copy = list.slice();
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Loads and caches the starter data asset. @type {Map<string, Promise<Reel[]>>} */
const fallbackCache = new Map();

/** @param {unknown} rows @returns {Reel[]} */
function normalizeReels(rows) {
  if (!Array.isArray(rows)) return [];
  const result = [];
  for (const row of rows) {
    if (!row || typeof row.id !== 'string' || !/^[\w-]{11}$/.test(row.id)) continue;
    const handles = [];
    for (const value of Array.isArray(row.p) ? row.p : [row.p]) {
      if (typeof value !== 'string' || !value.trim()) continue;
      let handle = value.trim();
      try { handle = decodeURIComponent(handle); } catch { /* Keep an already valid literal handle. */ }
      if (!handles.includes(handle)) handles.push(handle);
    }
    result.push({ id: row.id, t: typeof row.t === 'string' ? row.t : '', p: handles });
  }
  return result;
}

/** Explicit Shopify links take priority; a starter link fills an unlinked entry.
 * @param {unknown} primary @param {unknown} fallback @returns {Reel[]}
 */
export function mergeReels(primary, fallback) {
  const result = new Map();
  const authoritative = new Set();
  for (const reel of normalizeReels(primary)) {
    const existing = result.get(reel.id);
    if (existing) {
      existing.p = [...new Set([...existing.p, ...reel.p])];
      if (!existing.t) existing.t = reel.t;
    } else result.set(reel.id, reel);
    if (reel.p.length) authoritative.add(reel.id);
  }
  for (const reel of normalizeReels(fallback)) {
    const existing = result.get(reel.id);
    if (!existing) result.set(reel.id, reel);
    else {
      if (!authoritative.has(reel.id)) existing.p = [...new Set([...existing.p, ...reel.p])];
      if (!existing.t) existing.t = reel.t;
    }
  }
  return [...result.values()];
}

/** Keep the current book first in chips and the player for multi-book videos.
 * @param {Reel[]} reels @param {string} handle @returns {Reel[]}
 */
export function productReels(reels, handle) {
  return reels.filter((reel) => reel.p.includes(handle)).map((reel) => ({ ...reel, p: [handle, ...reel.p.filter((other) => other !== handle)] }));
}

/** @param {string} url */
function loadFallback(url) {
  if (!fallbackCache.has(url)) {
    fallbackCache.set(
      url,
      fetch(url)
        .then((r) => (r.ok ? r.json() : []))
        .then(normalizeReels)
        .catch(() => [])
    );
  }
  return /** @type {Promise<Reel[]>} */ (fallbackCache.get(url));
}

/** Every reel known on the page, shared by the viewer for its random continuation. @type {Map<string, Reel>} */
const allReels = new Map();

class IlmReels extends HTMLElement {
  /** @type {Reel[]} */
  reels = [];
  #events;
  #bookObserver;

  async connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.#events = new AbortController();
    const controller = this.#events;

    const meta = this.#readJson('[data-reels-meta]') || [];
    const contextHandles = this.#readJson('[data-reels-context]') || [];
    /** @type {Reel[]} */
    let list = mergeReels(meta, []);

    if (this.dataset.fallbackUrl) {
      const starter = await loadFallback(this.dataset.fallbackUrl);
      list = mergeReels(meta, starter);
    }
    if (!this.isConnected || controller.signal.aborted) return;
    for (const reel of list) allReels.set(reel.id, reel);

    const mode = this.dataset.context || 'all';
    /** @type {Reel[]} */
    let selected = list;
    if (mode === 'product' && this.dataset.product) {
      selected = productReels(list, this.dataset.product);
    } else if (mode === 'collection') {
      const set = new Set(contextHandles);
      selected = list.filter((r) => r.p.some((handle) => set.has(handle)));
    } else if (this.dataset.linkedFirst === 'true') {
      selected = shuffle(list.filter((r) => r.p.length)).concat(shuffle(list.filter((r) => !r.p.length)));
    }

    const limit = Number(this.dataset.limit) || 12;
    this.reels = selected.slice(0, limit);

    if (!this.reels.length) {
      if (this.dataset.hideEmpty === 'true') {
        (this.closest('.shopify-section') || this).setAttribute('hidden', '');
      }
      return;
    }
    this.#render();
    this.#maybeOpenFromUrl();
  }

  disconnectedCallback() {
    this.#events?.abort();
    this.#bookObserver?.disconnect();
    delete this.dataset.ready;
  }

  /** @param {string} selector */
  #readJson(selector) {
    const node = this.querySelector(selector);
    if (!node?.textContent) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  #render() {
    const track = this.querySelector('[data-reels-track]');
    if (!track) return;
    const signal = this.#events.signal;
    track.innerHTML = this.reels
      .map(
        (reel, index) => `
        <li class="ilm-reel-card" style="--reel-index:${index}">
          <button type="button" class="ilm-reel-card__button" data-reel-id="${escapeHtml(reel.id)}" aria-label="${escapeHtml(this.dataset.labelPlay || 'Play video')}${reel.t ? `: ${escapeHtml(reel.t)}` : ''}">
            <img class="ilm-reel-card__poster" src="${THUMB(reel.id)}" alt="" loading="${index < 4 ? 'eager' : 'lazy'}" decoding="async" width="360" height="640" data-fallback="${THUMB_FALLBACK(reel.id)}">
            <span class="ilm-reel-card__shade" aria-hidden="true"></span>
            <span class="ilm-reel-card__play" aria-hidden="true"><svg viewBox="0 0 24 24" width="22" height="22"><path d="M8 5.5v13l10.5-6.5z" fill="currentColor"/></svg></span>
            ${reel.t ? `<span class="ilm-reel-card__title" dir="auto">${escapeHtml(reel.t)}</span>` : ''}
          </button>
          ${reel.p.length ? `<a class="ilm-reel-card__book" data-book="${escapeHtml(reel.p[0])}" href="${(window.Shopify?.routes?.root || '/')}products/${encodeURIComponent(reel.p[0])}"><span class="ilm-reel-card__book-img"></span><span class="ilm-reel-card__book-text"><span class="ilm-reel-card__book-title"></span><span class="ilm-reel-card__book-price"></span></span></a>` : ''}
        </li>`
      )
      .join('');

    for (const img of track.querySelectorAll('img[data-fallback]')) {
      img.addEventListener(
        'error',
        () => {
          const fallback = img.getAttribute('data-fallback');
          if (fallback && img.getAttribute('src') !== fallback) img.setAttribute('src', fallback);
        },
        { once: true, signal }
      );
    }

    for (const type of ['pointerenter', 'touchstart', 'focusin']) {
      track.addEventListener(type, warmConnections, { once: true, passive: true, signal });
    }

    track.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      const button = target.closest('[data-reel-id]');
      if (!(button instanceof HTMLElement)) return;
      viewer().open(/** @type {string} */ (button.dataset.reelId), this.reels);
    }, { signal });

    // Fill book chips as cards approach the viewport.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          observer.unobserve(entry.target);
          const chip = /** @type {HTMLElement | null} */ (entry.target.querySelector('[data-book]'));
          if (!chip) continue;
          fetchBook(/** @type {string} */ (chip.dataset.book)).then((book) => {
            if (signal.aborted || !chip.isConnected) return;
            if (!book) {
              chip.remove();
              return;
            }
            const img = chip.querySelector('.ilm-reel-card__book-img');
            if (img && book.image) img.innerHTML = `<img src="${escapeHtml(book.image)}" alt="" width="40" height="40" loading="lazy">`;
            const title = chip.querySelector('.ilm-reel-card__book-title');
            if (title) title.textContent = book.title;
            const price = chip.querySelector('.ilm-reel-card__book-price');
            if (price) price.textContent = money(book.price);
            chip.classList.add('is-ready');
          });
        }
      },
      { rootMargin: '200px' }
    );
    this.#bookObserver = observer;
    // Observe the card (the chip itself is display:none until filled, which never intersects).
    for (const chip of track.querySelectorAll('[data-book]')) {
      const card = chip.closest('.ilm-reel-card');
      if (card) observer.observe(card);
    }

    this.querySelector('[data-reels-prev]')?.addEventListener('click', () => this.#scroll(-1), { signal });
    this.querySelector('[data-reels-next]')?.addEventListener('click', () => this.#scroll(1), { signal });
  }

  /** @param {number} direction */
  #scroll(direction) {
    const track = this.querySelector('[data-reels-track]');
    if (!(track instanceof HTMLElement)) return;
    const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    track.scrollBy({ left: direction * rtl * track.clientWidth * 0.8, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  #maybeOpenFromUrl() {
    const match = window.location.hash.match(/^#reel-([\w-]{11})$/);
    if (!match || IlmReels.openedFromUrl) return;
    const reel = allReels.get(match[1]);
    if (!reel) return;
    IlmReels.openedFromUrl = true;
    viewer().open(reel.id, this.reels);
  }

  static openedFromUrl = false;
}

const YT_ORIGIN = 'https://www.youtube.com';
const YT_ENDED = 0;
const YT_PLAYING = 1;
const YT_BUFFERING = 3;

let connectionsWarmed = false;
/** Opens connections to YouTube early (first hover/touch on a reel), so the first video starts sooner. */
function warmConnections() {
  if (connectionsWarmed) return;
  connectionsWarmed = true;
  for (const href of [YT_ORIGIN, 'https://www.google.com', 'https://i.ytimg.com']) {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = href;
    link.crossOrigin = '';
    document.head.append(link);
  }
}

/**
 * The viewer's one YouTube player, controlled through the iframe postMessage protocol (the same
 * messages the IFrame API uses, without loading its script). Reels are switched inside this
 * player with loadVideoById instead of creating a new embed per reel: phones only allow sound
 * in a player the visitor has unmuted, and the player keeps that choice for every later video.
 */
class YtPlayer {
  ready = false;
  state = -1;
  /** @type {boolean | null} - Whether the player is muted, as last reported by the player */
  muted = null;
  /** @type {object[]} */
  pending = [];
  /** @type {(() => void) | null} - Called when the playback state or mute state changes */
  onChange = null;

  /**
   * @param {string} id - First YouTube video id
   * @param {string} title - Accessible iframe title
   */
  constructor(id, title) {
    const params = new URLSearchParams({
      autoplay: '1',
      playsinline: '1',
      rel: '0',
      modestbranding: '1',
      controls: '1',
      enablejsapi: '1',
      origin: window.location.origin,
    });
    this.iframe = document.createElement('iframe');
    this.iframe.src = `${YT_ORIGIN}/embed/${id}?${params}`;
    this.iframe.title = title;
    this.iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    this.iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    this.iframe.allowFullscreen = true;
    this.iframe.addEventListener('load', this.#listen, { once: true });
    window.addEventListener('message', this.#onMessage);
  }

  #listen = () => {
    // The player ignores messages until it has initialised, so ask a few times until it answers.
    let tries = 0;
    const ping = () => {
      if (this.ready || tries++ > 20 || !this.iframe.isConnected) return;
      this.#post({ event: 'listening', id: 'ilm-reels', channel: 'widget' });
      setTimeout(ping, 250);
    };
    ping();
  };

  /** @param {MessageEvent} event */
  #onMessage = (event) => {
    if (event.origin !== YT_ORIGIN || event.source !== this.iframe.contentWindow || typeof event.data !== 'string') return;
    let data;
    try {
      data = JSON.parse(event.data);
    } catch {
      return;
    }
    if (data.event === 'onReady' && !this.ready) {
      this.ready = true;
      for (const message of this.pending.splice(0)) this.#post(message);
    }
    let changed = false;
    const info = data.event === 'infoDelivery' ? data.info : null;
    if (typeof info?.muted === 'boolean' && info.muted !== this.muted) {
      this.muted = info.muted;
      changed = true;
    }
    const state = data.event === 'onStateChange' ? data.info : info?.playerState;
    if (typeof state === 'number' && state !== this.state) {
      this.state = state;
      changed = true;
      // Loop the current reel.
      if (state === YT_ENDED) {
        this.command('seekTo', [0, true]);
        this.command('playVideo');
      }
    }
    if (changed) this.onChange?.();
  };

  /** @param {object} message */
  #post(message) {
    this.iframe.contentWindow?.postMessage(JSON.stringify(message), YT_ORIGIN);
  }

  /** @param {string} func @param {unknown[]} [args] */
  command(func, args = []) {
    const message = { event: 'command', func, args, id: 'ilm-reels', channel: 'widget' };
    if (this.ready) this.#post(message);
    else this.pending.push(message);
  }

  /** @param {string} id - Switch to this video, keeping the player's sound setting */
  load(id) {
    this.state = -1;
    this.command('loadVideoById', [id, 0]);
  }

  get playing() {
    return this.state === YT_PLAYING || this.state === YT_BUFFERING;
  }

  destroy() {
    window.removeEventListener('message', this.#onMessage);
    this.onChange = null;
    this.iframe.remove();
  }
}

/** The single full-screen viewer shared by every <ilm-reels> on the page. */
class ReelViewer {
  /** @type {HTMLDialogElement} */
  dialog;
  /** @type {HTMLElement} */
  feed;
  /** @type {Reel[]} */
  queue = [];
  rendered = 0;
  /** @type {HTMLElement | null} */
  active = null;
  /** @type {IntersectionObserver | null} */
  observer = null;
  /** @type {Element | null} */
  opener = null;
  pushedHistory = false;
  /** @type {YtPlayer | null} - One player for the whole feed, moved over the active reel */
  stage = null;
  /** @type {HTMLElement} */
  stageHost;
  /** @type {HTMLButtonElement} */
  soundButton;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  soundTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  revealTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  toastTimer;

  constructor() {
    const labels = /** @type {HTMLElement | null} */ (document.querySelector('ilm-reels'))?.dataset || {};
    this.labels = {
      close: labels.labelClose || 'Close',
      add: labels.labelAdd || 'Add to cart',
      view: labels.labelView || 'View book',
      choose: labels.labelChoose || 'Choose options',
      soldOut: labels.labelSoldOut || 'Sold out',
      added: labels.labelAdded || 'Added',
      adding: labels.labelAdding || 'Adding…',
      addedToast: labels.labelAddedToast || 'Added to your basket',
      viewCart: labels.labelViewCart || 'View basket',
      next: labels.labelNext || 'Next video',
      prev: labels.labelPrev || 'Previous video',
      browse: labels.labelBrowse || 'Browse books',
      dialog: labels.labelDialog || 'Shoppable videos',
      sound: labels.labelSound || 'Tap for sound',
    };

    this.dialog = document.createElement('dialog');
    this.dialog.className = 'ilm-reel-viewer';
    this.dialog.setAttribute('aria-label', this.labels.dialog);
    this.dialog.innerHTML = `
      <div class="ilm-reel-viewer__chrome">
        <button type="button" class="ilm-reel-viewer__close" data-close aria-label="${escapeHtml(this.labels.close)}">
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
        <button type="button" class="ilm-reel-viewer__sound" data-unmute hidden>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor"/><path d="M16.5 8.5l5 7m0-7l-5 7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          <span>${escapeHtml(this.labels.sound)}</span>
        </button>
        <div class="ilm-reel-viewer__toast" role="status" data-reel-toast hidden>
          <span data-reel-toast-text></span>
          <button type="button" data-view-cart>${escapeHtml(this.labels.viewCart)}</button>
        </div>
        <div class="ilm-reel-viewer__nav">
          <button type="button" data-step="-1" aria-label="${escapeHtml(this.labels.prev)}"><svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M6 15l6-6 6 6" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
          <button type="button" data-step="1" aria-label="${escapeHtml(this.labels.next)}"><svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
        </div>
      </div>
      <div class="ilm-reel-viewer__feed" data-feed tabindex="-1"></div>`;
    document.body.append(this.dialog);
    this.feed = /** @type {HTMLElement} */ (this.dialog.querySelector('[data-feed]'));
    this.soundButton = /** @type {HTMLButtonElement} */ (this.dialog.querySelector('[data-unmute]'));
    // The player lives inside the feed (so swipes over it still scroll the feed) and is positioned
    // over the active reel; it is never moved in the DOM, which would reload it.
    this.stageHost = document.createElement('div');
    this.stageHost.className = 'ilm-reel-viewer__stage';
    window.addEventListener('resize', () => this.#placeStage());

    this.dialog.addEventListener('click', this.#onClick);
    this.dialog.addEventListener('close', this.#onClose);
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'PageDown') {
        event.preventDefault();
        this.step(1);
      } else if (event.key === 'ArrowUp' || event.key === 'PageUp') {
        event.preventDefault();
        this.step(-1);
      }
    });
    this.feed.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('popstate', () => {
      if (this.dialog.open && !window.location.hash.startsWith('#reel-')) this.dialog.close();
    });
  }

  /**
   * @param {string} id - Reel to start with
   * @param {Reel[]} context - Reels from the rail that was clicked; they follow first, shuffled
   */
  open(id, context) {
    const first = context.find((r) => r.id === id) || allReels.get(id);
    if (!first) return;
    this.opener = document.activeElement;
    const contextRest = shuffle(context.filter((r) => r.id !== id));
    const seen = new Set([id, ...contextRest.map((r) => r.id)]);
    const others = shuffle([...allReels.values()].filter((r) => !seen.has(r.id)));
    this.queue = [first, ...contextRest, ...others];
    this.rendered = 0;
    this.feed.replaceChildren(this.stageHost);
    this.#renderMore(4);

    document.documentElement.classList.add('ilm-reels-open');
    this.dialog.showModal();
    this.feed.scrollTop = 0;
    this.pushedHistory = window.location.hash !== `#reel-${id}`;
    if (this.pushedHistory) history.pushState({ ilmReel: id }, '', `#reel-${id}`);
    this.#observe();
    requestAnimationFrame(() => this.#activate(this.#slides()[0] || null));
  }

  /** @param {number} count */
  #renderMore(count) {
    const slice = this.queue.slice(this.rendered, this.rendered + count);
    for (const reel of slice) {
      const slide = document.createElement('section');
      slide.className = 'ilm-reel-slide';
      slide.dataset.reelId = reel.id;
      slide.setAttribute('aria-roledescription', 'slide');
      slide.innerHTML = `
        <div class="ilm-reel-slide__frame">
          <img class="ilm-reel-slide__poster" src="${THUMB(reel.id)}" alt="" decoding="async">
          <div class="ilm-reel-slide__overlay">
            ${reel.t ? `<p class="ilm-reel-slide__caption" dir="auto">${escapeHtml(reel.t)}</p>` : ''}
            <div class="ilm-reel-slide__shop" data-shop="${escapeHtml(reel.p[0] || '')}">
              ${reel.p.length ? '<div class="ilm-reel-shop is-loading"></div>' : `<a class="ilm-reel-shop ilm-reel-shop--browse" href="${(window.Shopify?.routes?.root || '/')}collections/all">${escapeHtml(this.labels.browse)}</a>`}
            </div>
          </div>
        </div>`;
      this.feed.append(slide);
      this.observer?.observe(slide);
      if (reel.p.length) this.#fillShop(slide, reel.p[0]);
    }
    this.rendered += slice.length;
  }

  /** @param {HTMLElement} slide @param {string} handle */
  async #fillShop(slide, handle) {
    const box = slide.querySelector('.ilm-reel-shop');
    if (!box) return;
    const book = await fetchBook(handle);
    if (!book) {
      box.outerHTML = `<a class="ilm-reel-shop ilm-reel-shop--browse" href="${(window.Shopify?.routes?.root || '/')}collections/all">${escapeHtml(this.labels.browse)}</a>`;
      return;
    }
    const action = !book.available
      ? `<span class="ilm-reel-shop__btn is-disabled">${escapeHtml(this.labels.soldOut)}</span>`
      : book.multiple
        ? `<a class="ilm-reel-shop__btn" href="${escapeHtml(book.url)}">${escapeHtml(this.labels.choose)}</a>`
        : `<button type="button" class="ilm-reel-shop__btn" data-add="${book.variantId}">${escapeHtml(this.labels.add)}</button>`;
    box.classList.remove('is-loading');
    box.innerHTML = `
      <a class="ilm-reel-shop__book" href="${escapeHtml(book.url)}">
        ${book.image ? `<img src="${escapeHtml(book.image)}" alt="" width="56" height="56">` : ''}
        <span class="ilm-reel-shop__text">
          <span class="ilm-reel-shop__title" dir="auto">${escapeHtml(book.title)}</span>
          <span class="ilm-reel-shop__price">${money(book.price)}${book.compare > book.price ? ` <s>${money(book.compare)}</s>` : ''}</span>
        </span>
      </a>
      <div class="ilm-reel-shop__actions">
        ${action}
        <a class="ilm-reel-shop__link" href="${escapeHtml(book.url)}">${escapeHtml(this.labels.view)}</a>
      </div>`;
  }

  #observe() {
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) this.#activate(/** @type {HTMLElement} */ (entry.target));
        }
      },
      { root: this.feed, threshold: [0.6] }
    );
    for (const slide of this.#slides()) this.observer.observe(slide);
  }

  /** @returns {HTMLElement[]} */
  #slides() {
    return /** @type {HTMLElement[]} */ ([...this.feed.querySelectorAll(':scope > .ilm-reel-slide')]);
  }

  /** @param {HTMLElement | null} slide */
  #activate(slide) {
    if (!slide || slide === this.active) return;
    this.active?.classList.remove('is-active');
    this.active = slide;
    slide.classList.add('is-active');
    const id = slide.dataset.reelId;
    if (!id) return;

    // Show the reel's cover until its video is playing, then reveal the player.
    this.stageHost.classList.add('is-loading');
    this.#placeStage();
    if (this.stage) {
      this.stage.load(id);
    } else {
      this.stage = new YtPlayer(id, this.labels.dialog);
      this.stage.onChange = this.#onStageChange;
      this.stageHost.append(this.stage.iframe);
    }
    this.#watch(slide);

    if (window.location.hash !== `#reel-${id}`) history.replaceState({ ilmReel: id }, '', `#reel-${id}`);
    // Keep a few slides ahead rendered so scrolling never runs out.
    const index = this.#slides().indexOf(slide);
    if (index >= this.rendered - 2 && this.rendered < this.queue.length) this.#renderMore(4);
  }

  /** Positions the player over the active reel's frame. */
  #placeStage() {
    const frame = /** @type {HTMLElement | null} */ (this.active?.querySelector('.ilm-reel-slide__frame'));
    if (!frame) return;
    Object.assign(this.stageHost.style, {
      top: `${frame.offsetTop}px`,
      left: `${frame.offsetLeft}px`,
      width: `${frame.offsetWidth}px`,
      height: `${frame.offsetHeight}px`,
    });
  }

  /**
   * If a reel hasn't started shortly after it was shown (the browser blocked playback with sound,
   * common on iPhone before the first tap), play it muted and offer "Tap for sound". Once the
   * visitor turns sound on, the same player keeps it for every following reel.
   * @param {HTMLElement} slide
   */
  #watch(slide) {
    clearTimeout(this.soundTimer);
    clearTimeout(this.revealTimer);
    // Reveal the player even if it hasn't started, so its own play button is reachable.
    this.revealTimer = setTimeout(() => this.stageHost.classList.remove('is-loading'), 1500);
    this.soundTimer = setTimeout(() => {
      const stage = this.stage;
      if (slide !== this.active || !stage?.ready || stage.playing) return;
      stage.command('mute');
      stage.command('playVideo');
    }, 1500);
  }

  #onStageChange = () => {
    const stage = this.stage;
    if (!stage) return;
    if (stage.state === YT_PLAYING) this.stageHost.classList.remove('is-loading');
    this.soundButton.hidden = !(stage.muted && stage.playing);
  };

  /** @param {number} direction */
  step(direction) {
    const slides = this.#slides();
    const index = this.active ? slides.indexOf(this.active) : 0;
    const target = slides[Math.max(0, Math.min(slides.length - 1, index + direction))];
    target?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  }

  #onScroll = () => {
    if (this.feed.scrollTop + this.feed.clientHeight * 2 > this.feed.scrollHeight && this.rendered < this.queue.length) {
      this.#renderMore(4);
    }
  };

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-close]')) {
      this.dialog.close();
      return;
    }
    if (target.closest('[data-unmute]')) {
      this.stage?.command('unMute');
      this.stage?.command('playVideo');
      this.soundButton.hidden = true;
      return;
    }
    const stepButton = target.closest('[data-step]');
    if (stepButton instanceof HTMLElement) {
      this.step(Number(stepButton.dataset.step));
      return;
    }
    if (target.closest('[data-view-cart]')) {
      this.#openCart();
      return;
    }
    const add = target.closest('[data-add]');
    if (add instanceof HTMLButtonElement) {
      await this.#addToCart(add);
    }
  };

  /**
   * Adds the reel's book without leaving the video: the button confirms, a small banner offers
   * "View basket", and the cart count updates (the cart drawer stays closed).
   * @param {HTMLButtonElement} button
   */
  async #addToCart(button) {
    if (button.disabled) return;
    button.disabled = true;
    const original = button.textContent;
    button.textContent = this.labels.adding;
    try {
      await addToCart([{ id: String(button.dataset.add), quantity: 1 }], { source: 'ilm-reels', openCart: false });
      button.textContent = this.labels.added;
      button.classList.add('is-added');
      this.#toast(this.labels.addedToast);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(original);
      button.textContent = message;
      this.#toast(message);
      setTimeout(() => {
        button.textContent = original;
      }, 3000);
    } finally {
      button.disabled = false;
    }
  }

  /** @param {string} message */
  #toast(message) {
    const toast = /** @type {HTMLElement | null} */ (this.dialog.querySelector('[data-reel-toast]'));
    if (!toast) return;
    const text = toast.querySelector('[data-reel-toast-text]');
    if (text) text.textContent = message;
    toast.hidden = false;
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      toast.hidden = true;
    }, 4500);
  }

  #openCart() {
    this.dialog.close();
    const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    } else {
      // eslint-disable-next-line no-undef
      window.location.href = (typeof Theme !== 'undefined' && Theme.routes?.cart_url) || '/cart';
    }
  }

  #onClose = () => {
    clearTimeout(this.soundTimer);
    clearTimeout(this.revealTimer);
    this.stage?.destroy();
    this.stage = null;
    this.soundButton.hidden = true;
    this.active?.classList.remove('is-active');
    this.active = null;
    this.observer?.disconnect();
    this.feed.innerHTML = '';
    document.documentElement.classList.remove('ilm-reels-open');
    if (window.location.hash.startsWith('#reel-')) {
      if (this.pushedHistory) history.back();
      else history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    this.pushedHistory = false;
    if (this.opener instanceof HTMLElement) this.opener.focus({ preventScroll: true });
  };
}

/** @type {ReelViewer | null} */
let viewerInstance = null;
function viewer() {
  if (!viewerInstance) viewerInstance = new ReelViewer();
  return viewerInstance;
}

if (!customElements.get('ilm-reels')) customElements.define('ilm-reels', IlmReels);
